/* global analytics */
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router'
import cloneDeep from 'lodash/cloneDeep'
import { RouterState } from 'connected-react-router'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { useUtmParams } from 'utils/utmParams'

import {
  useGetBucketListQuery,
  useGetBucketListItemsQuery,
  useGetBucketListMapResultsQuery,
} from '../../hooks/useQueries'
import { resetFilters } from '../../actions/myBucketlistActions'
import { deserializeSearchQuery } from '../../actions/utils/search'

import LazyImage from '../../components/display/LazyImage'
import DisableScrollAnchoring from '../../components/utils/DisableScrollAnchoring'
import useWhichMapMode from '../../components/layout/ItemGridWithMap/useWhichMapMode'
import DatesFilter from '../../search/filters/mobile/DatesFilter'
import PeopleFilterPanel from '../../search/filters/mobile/PeopleFilter'
import { MobileFiltersInner } from '../../components/layout/ItemGridWithMap/Mobile'
import { MobileFilterButton } from '../../search/MobileSearchView'
import { normalizeKeys } from '../../components/utils/propsUtils'

import Map from './Map'
import DesktopBucketListFilters from './DesktopBucketListFilters'
import DesktopView from './DesktopView'
import MobileView from './MobileView'
import Items from './Items'
import useBucketListPageLayout from './useBucketListPageLayout'
import {
  Actions,
  BucketListName,
  DesktopActions,
  Header,
  Overlay,
} from './shared'
import { formatNameNarrow } from './helpers'

interface Filters {
  adults?: number
  children?: number
  startDate?: string
  endDate?: string
}

interface BucketListProps {
  filters: Filters
  dispatch: (...args: any) => any
  searchQuery?: string
}

type FilterPanelName = 'dates' | 'people' | 'adventure_type' | null

const Bucketlist = ({ dispatch, filters, searchQuery }: BucketListProps) => {
  useBucketListPageLayout()

  const { md: isMd } = useMediaQueryContext()

  const mapMode = useWhichMapMode()
  const inlineMapMode = mapMode === 'inline'

  const { token } = useParams() as { token: string }

  const [openPanel, setOpenPanel] = useState(null) as [
    FilterPanelName,
    (arg0: FilterPanelName) => any
  ]
  const handleClosePanel = () => setOpenPanel(null)
  const handleResetFilters = () => dispatch(resetFilters())

  const {
    loading: bucketListLoading,
    data: bucketListData,
  } = useGetBucketListQuery({
    variables: {
      token,
    },
  })
  const user = bucketListData?.bucketList?.user

  const itemsPerFetch = 12
  const {
    loading: accommodationLoading,
    data: accommodationData,
    fetchMore: fetchMoreAccomodation,
  } = useGetBucketListItemsQuery({
    variables: {
      token,
      first: itemsPerFetch,
      params: {
        ...filters,
        type: 'accommodation',
      },
    },
  })

  const {
    loading: adventuresLoading,
    data: adventuresData,
    fetchMore: fetchMoreAdventures,
  } = useGetBucketListItemsQuery({
    variables: {
      token,
      first: itemsPerFetch,
      params: {
        ...filters,
        type: 'adventures',
      },
    },
  })

  const {
    loading: mapLoading,
    data: mapData,
  } = useGetBucketListMapResultsQuery({
    variables: {
      token,
      params: filters,
    },
  })
  const mapResults = mapData?.bucketList?.mapResults || []

  const utmParams = useUtmParams()

  useEffect(() => {
    if (user) {
      analytics.track('Public Bucket List Viewed', {
        owner_id: user.id,
        ...utmParams,
      })
    }
  }, [user])

  const handleSeeMoreAccommodation = () => {
    fetchMoreAccomodation({
      variables: {
        cursor: accommodationData.bucketList.items.pageInfo.endCursor,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prevResult

        const { edges: previousEdges } = prevResult.bucketList.items
        const {
          edges: nextEdges,
          pageInfo: nextPageInfo,
        } = fetchMoreResult.bucketList.items

        const nextResult = cloneDeep(prevResult)
        nextResult.bucketList.items.pageInfo = nextPageInfo
        nextResult.bucketList.items.edges = [...previousEdges, ...nextEdges]
        return nextResult
      },
    })
  }

  const handleSeeMoreAdventures = () => {
    fetchMoreAdventures({
      variables: {
        cursor: adventuresData.bucketList.items.pageInfo.endCursor,
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        const nextResult = cloneDeep(prevResult)
        if (!fetchMoreResult) return nextResult

        const { edges: previousEdges } = prevResult.bucketList.items
        const {
          edges: nextEdges,
          pageInfo: nextPageInfo,
        } = fetchMoreResult.bucketList.items

        nextResult.bucketList.items.pageInfo = nextPageInfo
        nextResult.bucketList.items.edges = [...previousEdges, ...nextEdges]
        return nextResult
      },
    })
  }

  const accommodationHasNextPage =
    accommodationData?.bucketList?.items?.pageInfo?.hasNextPage
  const adventuresHasNextPage =
    adventuresData?.bucketList?.items?.pageInfo?.hasNextPage

  const accommodationNodes = accommodationData?.bucketList?.items?.edges?.map(
    ({ node }) => node
  )
  const adventuresNodes = adventuresData?.bucketList?.items?.edges?.map(
    ({ node }) => node
  )

  const hasFilters = Object.keys(filters).length > 0
  const noAccommodation = accommodationNodes?.length === 0
  const noAdventures = adventuresNodes?.length === 0
  const noNodes = noAccommodation && noAdventures
  const bucketListEmpty = !hasFilters && noNodes

  const themeImage = bucketListData?.bucketList?.theme?.image
  const headerImageSrc = isMd ? themeImage?.hero : themeImage?.large
  const headerBucketListName = isMd
    ? bucketListData?.bucketList?.name
    : formatNameNarrow(bucketListData?.bucketList?.name)

  const hideFilters =
    bucketListData?.bucketList?.includesAccommodation === false

  const mapComponent = (
    <Map
      results={mapResults}
      loading={mapLoading}
      filters={filters}
      searchQuery={searchQuery}
      mobile={mapMode === 'toggle'}
    />
  )

  const itemsComponent = (
    <Items
      accommodationLoading={accommodationLoading}
      accommodationNodes={accommodationNodes}
      accommodationHasNextPage={accommodationHasNextPage}
      handleSeeMoreAccommodation={handleSeeMoreAccommodation}
      adventuresLoading={adventuresLoading}
      adventuresNodes={adventuresNodes}
      adventuresHasNextPage={adventuresHasNextPage}
      handleSeeMoreAdventures={handleSeeMoreAdventures}
      bucketListLoading={bucketListLoading}
      bucketListEmpty={bucketListEmpty}
      handleResetFilters={handleResetFilters}
      publicBucketlist
    />
  )

  const filterBarComponent =
    bucketListEmpty !== true &&
    (isMd ? (
      <DesktopActions>
        {!hideFilters && <DesktopBucketListFilters />}
      </DesktopActions>
    ) : (
      <Actions>
        <MobileFiltersInner>
          {!hideFilters && (
            <>
              <MobileFilterButton
                onClick={() => setOpenPanel('dates')}
                active={!!filters.startDate || !!filters.endDate}
              >
                Dates
              </MobileFilterButton>
              <MobileFilterButton
                onClick={() => setOpenPanel('people')}
                active={!!filters.adults || !!filters.children}
              >
                People
              </MobileFilterButton>
            </>
          )}
        </MobileFiltersInner>
      </Actions>
    ))

  return (
    <>
      <DisableScrollAnchoring />
      <Header>
        <Overlay />
        <BucketListName>{headerBucketListName}</BucketListName>
        <LazyImage data-src={!bucketListLoading && headerImageSrc} preload />
      </Header>

      {inlineMapMode ? (
        <DesktopView
          filterBarComponent={filterBarComponent}
          mapComponent={mapComponent}
          resultsComponent={itemsComponent}
        />
      ) : (
        <MobileView
          filterBarComponent={filterBarComponent}
          mapComponent={mapComponent}
          resultsComponent={itemsComponent}
        />
      )}

      <DatesFilter
        filters={filters}
        open={openPanel === 'dates'}
        onClose={handleClosePanel}
      />

      <PeopleFilterPanel
        filters={filters}
        open={openPanel === 'people'}
        onClose={handleClosePanel}
      />
    </>
  )
}

interface ReduxState {
  router: RouterState
}

export default connect(({ router }: ReduxState) => ({
  filters: normalizeKeys(deserializeSearchQuery(router.location.search)),
  searchQuery: router.location.search,
}))(Bucketlist)
